import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EmptyState, ErrorState, LoadingBlock } from "@/components/states";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import {
  getLocalizedField,
  isDeliverableInCountry,
  isOrderable,
  minOrder,
  normalizeQuantity,
  packSize,
  productImages,
  stockLevel,
} from "@/lib/product";
import { PdpBlock } from "@/components/PdpBlocks";
import { mobielBasis, resolveTemplate } from "@/lib/pdpTemplate";
import { blokkenVoorWeergave } from "@/lib/weergave";
import { usePdpTemplates } from "@/hooks/usePdpTemplates";
import { useMobiel } from "@/hooks/useMobiel";
import { useApiError } from "@/lib/useApiError";
import { cmsCall } from "@/lib/cms";
import { MobielKoopbalk } from "@/components/PdpKoopblok";

const ProductDetail = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { product: fetchProduct, account, isAuthenticated, booting } = useAuth();
  const { addLine } = useCart();
  const navigate = useNavigate();
  const toMessage = useApiError();
  const templates = usePdpTemplates();
  const mobiel = useMobiel();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [aantal, setAantal] = useState("1");
  const [activeImage, setActiveImage] = useState(0);

  const laadNr = useRef(0);
  const load = useCallback(async () => {
    if (booting) return;
    const nr = ++laadNr.current;
    setLoading(true);
    setError("");
    setNotFound(false);
    try {
      // niet ingelogd: publieke productdata (zonder prijs/voorraad) via de eigen backend
      const data = isAuthenticated ? await fetchProduct(id) : await cmsCall("GET", `/content/product/${id}`);
      if (nr !== laadNr.current) return;
      setProduct(data);
      // Start op de verkoophoeveelheid uit het CRM (besteleenheid / minimale afname).
      setAantal(String(normalizeQuantity(data, minOrder(data) || packSize(data) || 1)));
      setActiveImage(0);
    } catch (err) {
      if (nr !== laadNr.current) return;
      if (err?.status === 404) setNotFound(true);
      else setError(toMessage(err));
    } finally {
      if (nr === laadNr.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchProduct, id, isAuthenticated, booting]);

  useEffect(() => {
    load();
  }, [load]);


  if (loading) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <LoadingBlock />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8" data-testid="product-not-found">
        <EmptyState
          title={t("product.notFound")}
          actionLabel={t("product.notFoundAction")}
          onAction={() => navigate("/producten")}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
        <ErrorState message={error} onRetry={load} />
      </div>
    );
  }

  const lang = i18n.language;
  const naam = getLocalizedField(product, "naam", lang);
  const omschrijving = getLocalizedField(product, "omschrijving", lang);
  const images = productImages(product);
  const outOfStock = stockLevel(product) === "out";
  const supplier = stockLevel(product) === "supplier";
  const deliverable = isDeliverableInCountry(product, account);
  const orderable = isOrderable(product, account) && deliverable;


  const onAdd = () => {
    if (!orderable) return;
    const qty = normalizeQuantity(product, aantal);
    addLine(product, qty);
    if (String(qty) !== String(aantal)) {
      setAantal(String(qty));
      toast.success(t("cart.quantityAdjusted", { aantal: qty }));
    }
    toast.success(t("product.added", { naam }));
  };

  const template = resolveTemplate(templates, product);
  const blokken = template.blokken;
  const ctx = {
    t,
    lang,
    account,
    naam,
    omschrijving,
    images,
    activeImage,
    setActiveImage,
    aantal,
    setAantal,
    onAdd,
    orderable,
    deliverable,
    outOfStock,
    supplier,
  };
  const kolom = (b) => b.kolom || "rechts";
  const linksBlokken = blokken.filter((b) => kolom(b) === "links");
  const rechtsBlokken = blokken.filter((b) => kolom(b) === "rechts");
  const onderBlokken = blokken.filter((b) => kolom(b) === "onder");
  // mobiel: gallery bovenaan, daarna alles in één kaart die over de foto schuift; volle-breedte blokken eronder
  const mobielLijst = mobiel ? blokkenVoorWeergave(mobielBasis(blokken), template.mobiel, true) : [];
  const mobielGallery = mobielLijst.filter((b) => b.type === "gallery");
  const mobielKaart = mobielLijst.filter((b) => b.type !== "gallery" && kolom(b) !== "onder");
  const mobielOnder = mobielLijst.filter((b) => kolom(b) === "onder");
  const blokKey = (b) => b.id || b.type;

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 pb-28 sm:px-6 sm:pb-10 lg:px-8 lg:py-10" data-testid="product-detail-page">
      <Button variant="ghost" className="px-0 text-muted-foreground" asChild data-testid="product-back-link">
        <Link to={account ? "/producten" : "/assortiment"}>
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
          {t("product.back")}
        </Link>
      </Button>

      {mobiel ? (
        <div className="mt-4" data-testid="pdp-column-mobile">
          <div className="-mx-4">
            {mobielGallery.map((blok) => <PdpBlock key={blokKey(blok)} blok={blok} product={product} ctx={ctx} />)}
          </div>
          <Card className="relative z-10 -mt-6 space-y-5 rounded-2xl p-5 shadow-[0_-10px_30px_-18px_rgba(0,0,0,0.35)]" data-testid="pdp-mobile-card">
            {mobielKaart.map((blok) => <PdpBlock key={blokKey(blok)} blok={blok} product={product} ctx={ctx} />)}
          </Card>
          {mobielOnder.map((blok) => <PdpBlock key={blokKey(blok)} blok={blok} product={product} ctx={ctx} />)}
          <MobielKoopbalk product={product} ctx={ctx} />
        </div>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="space-y-6 lg:col-span-7" data-testid="pdp-column-left">
              {linksBlokken.map((blok) => <PdpBlock key={blokKey(blok)} blok={blok} product={product} ctx={ctx} />)}
            </div>
            <div className="lg:col-span-5" data-testid="pdp-column-right">
              <Card className="space-y-5 p-6 lg:sticky lg:top-24">
                {rechtsBlokken.map((blok) => <PdpBlock key={blokKey(blok)} blok={blok} product={product} ctx={ctx} />)}
              </Card>
            </div>
          </div>
          {onderBlokken.map((blok) => <PdpBlock key={blokKey(blok)} blok={blok} product={product} ctx={ctx} />)}
        </>
      )}
    </div>
  );
};

export default ProductDetail;
